// #region Constants

const METER_TRACK_KIND      = 'metadata';
const METER_TRACK_LABEL     = 'audioMeters';
const TIMECODE_TRACK_LABEL  = 'timecode';

const MAX_CUES_TO_AVERAGE   = 10;

// #endregion Constants

// #region Operations

/**
 * Get the audio meters track from the specified video element. This track contains
 * all the cues with the data to render a waveform.
 *
 * @param element The element from which to extract the track.
 */
export function getAudioMetersTrack(element, trackIndex) {
  for (let track of element.textTracks) {
    if (
      (track.kind === METER_TRACK_KIND) &&
      (track.label === `${METER_TRACK_LABEL}-${trackIndex}`)
    ) {
      return track;
    }
  }

  return undefined;
}

/**
 * Gets the metadata track from the specified video element. This is the same
 * track that holds timecode information.
 * 
 * @param {HTMLVideoElement} element Where to extract the track from.
 * @returns The metadata track container in the video element.
 */
export function getMetadataTrack(element) {
  for (let track of element.textTracks) {
    if (
      (track.kind === METER_TRACK_KIND) &&
      (track.label === TIMECODE_TRACK_LABEL)
    ) {
      return track;
    }
  }

  return undefined;
}

/**
 * Get the cues from the specified track that fall into the specified range.
 *
 * @param track The track with the cues that will be filtered.
 * @param startTime Cues will all start after this this time.
 * @param endTime  Cues will all start before this this time.
 */
export function filterCues(track, startTime, endTime) {
  if (!track?.cues) {
    return [];
  }

  const startCueIndex = findClosestCueIndex(track, startTime, 0, track.cues?.length - 1);
  if (startCueIndex < 0) {
    return [];
  }

  const cues = [];
  let cueIndex = startCueIndex;
  while (cueIndex < track.cues.length && track.cues[cueIndex].startTime <= endTime) {
    cues.push(track.cues[cueIndex]);
    cueIndex++;
  }

  return cues;
}

/**
 * Creates ranges in the between the specified start and end time using the meter
 * cues in the specified track.
 * 
 * @param {*} track 
 * @param {*} startTime 
 * @param {*} endTime 
 * @param {*} interval 
 * @returns 
 */
export function collapseCues(track, startTime, endTime, interval) {
  if (!track?.cues || track.cues.length === 0) {
    return[]
  }

  const cues = [];
  const cueLength = track.cues[0].endTime - track.cues[0].startTime;

  // Limit the number of cues to average:
  const countEstimate = interval / cueLength;
  const cueStepSize = Math.max(1, Math.round(countEstimate / MAX_CUES_TO_AVERAGE));

  let offset = Math.floor(startTime / interval) * interval;
  let cueIndex = findClosestCueIndex(track, offset, 0, track.cues?.length - 1);

  while (offset < endTime) {
    let min = 0;
    let max = 0;
    let count = 0;

    // Note that this depends on the cues already being in order:
    while (cueIndex < track.cues.length && track.cues[cueIndex].startTime < (offset + interval)) {
      min += track.cues[cueIndex].value.meter.minMin;
      max += track.cues[cueIndex].value.meter.maxMax;
      count++;

      cueIndex += cueStepSize;
    }

    cues.push({
      startTime: offset,
      endTime:   offset + interval,
      value:     {
        meter: {
          minMin: count > 0 ? min / count : 0,
          maxMax: count > 0 ? max / count : 0
        }
      }
    });

    offset += interval;
  }

  return cues;
}

/**
 * Finds the index of the cue closest to the specified time. Note that this does
 * not mean the cue will start before the specified time and end after it. This is
 * to help find a cue when others have not loaded yet.
 *
 * @param track The track in which to find the cue.
 * @param time The time for which to find the cue.
 * @param startIndex The start index for the search.
 * @param endIndex The end index for the search.
 */
export function findClosestCueIndex(track, time, startIndex, endIndex) {
  if (endIndex < startIndex || !track.cues) {
    return startIndex;
  }

  const midIndex = Math.floor((startIndex + endIndex) / 2);
  const middleCue = track.cues[midIndex];

  if (middleCue.startTime <= time && middleCue.endTime >= time) {
    return midIndex;
  } else if (middleCue.startTime > time) {
    return findClosestCueIndex(track, time, startIndex, midIndex - 1);
  } else if (middleCue.endTime < time) {
    return findClosestCueIndex(track, time, midIndex + 1, endIndex);
  } else {
    return -1;
  }
}

// #endregion Operations
